import { Check, Edit } from "@mui/icons-material";
import {
	IconButton,
	InputAdornment,
	TextField,
	TextFieldProps,
	Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";

interface PercentFieldProps extends Omit<TextFieldProps, "onChange"> {
	value: number | null | undefined;
	onChange?: (value: number | null) => void;
	warning?: boolean;
	mustAllowEditable?: boolean;
	maxValue?: number | null;
}

// Format percent value with conditional decimals
const formatPercent = (
	value: number | null | undefined,
	showDecimals = true,
): string => {
	if (value === null || value === undefined || value === 0) return "";
	if (showDecimals) {
		return value.toFixed(2);
	}
	return String(value);
};

// Parse percent string to number, ensuring it respects configured bounds
const parsePercent = (
	value: string,
	{ maxValue = 100 }: { maxValue?: number | null } = {},
): number => {
	const cleaned = value.replace(/[^0-9.]/g, "");
	const parsed = parseFloat(cleaned);
	if (isNaN(parsed)) return 0;
	// Clamp value to the configured lower/upper bounds
	return maxValue == null
		? Math.max(parsed, 0)
		: Math.min(Math.max(parsed, 0), maxValue);
};

export const PercentField = React.forwardRef<
	HTMLInputElement,
	PercentFieldProps
>(
	(
		{
			value,
			onChange = () => {},
			warning,
			mustAllowEditable = false,
			maxValue = 100,
			disabled,
			onBlur,
			onFocus,
			...props
		},
		ref,
	) => {
		const [displayValue, setDisplayValue] = useState(
			formatPercent(value, false),
		);
		const [isFocused, setIsFocused] = useState(false);
		const [isEditable, setIsEditable] = useState(!mustAllowEditable);

		// Update display when value changes externally (not while user is typing)
		useEffect(() => {
			if (isFocused) return;
			if (value) {
				const hasDecimalPoint = String(value).includes(".");
				setDisplayValue(formatPercent(value, hasDecimalPoint));
			} else {
				setDisplayValue("");
			}
		}, [value, isFocused]);

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const inputValue = e.target.value;
			// Only allow digits and dot
			const filtered = inputValue.replace(/[^0-9.]/g, "");

			setDisplayValue(filtered);
			const parsed = parsePercent(filtered, { maxValue });
			onChange(parsed || null);
		};

		const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
			setIsFocused(false);
			const inputValue = e.target.value;
			const parsed = parsePercent(inputValue, { maxValue });

			const hasDecimalPoint = inputValue.includes(".");
			setDisplayValue(formatPercent(parsed, hasDecimalPoint));
			onChange(parsed || null);
			onBlur?.(e);
		};

		const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
			setIsFocused(true);
			// Show raw value on focus for easier editing
			setDisplayValue(String(parsePercent(displayValue, { maxValue })));
			onFocus?.(e);
			setTimeout(() => {
				e?.target.select();
			}, 0);
		};

		return (
			<TextField
				ref={ref}
				type="text"
				value={displayValue}
				onChange={props["aria-readonly"] ? () => {} : handleChange}
				onBlur={handleBlur}
				onFocus={handleFocus}
				disabled={!isEditable || disabled}
				margin="none"
				helperText={props["aria-readonly"] ? "" : props["helperText"]}
				slotProps={{
					htmlInput: {
						"data-test-id": props.label
							?.toString()
							.toLowerCase()
							.replaceAll(" ", "_"),
					},
					input: {
						endAdornment: (
							<>
								<InputAdornment position="end">%</InputAdornment>
								{mustAllowEditable ? (
									<InputAdornment position="end">
										<Tooltip
											title={isEditable ? "Lock Editing" : "Enable Editing"}
										>
											<IconButton
												onClick={() => setIsEditable(!isEditable)}
												edge="end"
												size="small"
											>
												{!isEditable ? (
													<Edit sx={{ fontSize: "1rem" }} />
												) : (
													<Check sx={{ fontSize: "1rem" }} />
												)}
											</IconButton>
										</Tooltip>
									</InputAdornment>
								) : null}
							</>
						),
					},
					formHelperText: {
						sx: {
							color: (theme) =>
								`${
									warning
										? theme.palette.warning.main
										: theme.palette.primary.main
								} ${!props.error ? "!important" : ""}`,
						},
					},
					...props?.slotProps,
				}}
				inputProps={{
					step: 0.01,
					min: 0,
					...(maxValue == null ? {} : { max: maxValue }),
				}}
				{...props}
			/>
		);
	},
);

PercentField.displayName = "PercentField";
